import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Image as ImageIcon, Instagram, Upload, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsAdmin = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_banner_image: '',
    instagram_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings/`);
      setSettings(response.data);
      setFormData({
        hero_title: response.data.hero_title,
        hero_subtitle: response.data.hero_subtitle,
        hero_banner_image: response.data.hero_banner_image || '',
        instagram_url: response.data.instagram_url
      });
    } catch (error) {
      toast.error('Failed to load settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/products/upload-image`, formDataUpload);
      
      setFormData({ ...formData, hero_banner_image: response.data.url });
      toast.success('Banner image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${API}/settings/`, formData);
      toast.success('Settings updated successfully!');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
      console.error(error);
    }
  };

  if (loading) {
    return <div className="loading-state">Loading settings...</div>;
  }

  return (
    <div className="settings-admin">
      <Card className="settings-card">
        <CardContent className="settings-content">
          <div className="settings-header">
            <SettingsIcon size={24} />
            <h2>Site Settings</h2>
          </div>

          <form onSubmit={handleSubmit} className="settings-form">
            <div className="form-group">
              <label>Hero Title</label>
              <input
                type="text"
                value={formData.hero_title}
                onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                placeholder="From Waste to Wellness"
              />
            </div>

            <div className="form-group">
              <label>Hero Subtitle</label>
              <textarea
                value={formData.hero_subtitle}
                onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                placeholder="Eco-friendly scented camphor pouches..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Instagram URL</label>
              <div className="input-with-icon">
                <Instagram size={20} />
                <input
                  type="url"
                  value={formData.instagram_url}
                  onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                  placeholder="https://www.instagram.com/soletosoul_27"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Hero Banner Image</label>
              {formData.hero_banner_image ? (
                <div className="banner-preview">
                  <img src={formData.hero_banner_image} alt="Hero banner" />
                  <button
                    type="button"
                    className="remove-banner"
                    onClick={() => setFormData({ ...formData, hero_banner_image: '' })}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="upload-banner">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  <Upload size={32} />
                  <span>{uploading ? 'Uploading...' : 'Upload Hero Banner'}</span>
                </label>
              )}
              <input
                type="text"
                value={formData.hero_banner_image}
                onChange={(e) => setFormData({ ...formData, hero_banner_image: e.target.value })}
                placeholder="Or paste image URL"
                className="image-url-input"
                style={{ marginTop: '8px' }}
              />
            </div>

            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
              Save Settings
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsAdmin;